/**
 * Layout Component
 *
 * Shared layout wrapper that provides consistent structure across all pages.
 * Uses React Router's Outlet for rendering child route content.
 *
 * Because ViteReactSSG owns the Router, this component (the root route element)
 * is also where we run app-wide, client-only effects:
 * - Restore the serif font variant from localStorage
 * - Scroll to top on route change
 * - Send GA4 page_view events on client-side navigation
 *
 * Structure:
 * - Fixed Header (navigation)
 * - Main content area (Outlet for page components)
 * - Footer
 */

import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import Header from './Header'
import Footer from './Footer'

const SERIF_STORAGE_KEY = 'recreo-serif-variant'
const GA_MEASUREMENT_ID = 'G-E33N5VXJ7W'

const Layout = () => {
  const { pathname, search } = useLocation()
  const isInitialLoad = useRef(true)

  // Restore the serif font variant once on mount.
  useEffect(() => {
    const saved = localStorage.getItem(SERIF_STORAGE_KEY) || 'garamond'
    document.documentElement.setAttribute('data-serif-variant', saved)
  }, [])

  // Scroll to top + GA4 page_view on route change. The initial page load is
  // already tracked by the gtag snippet in index.html, so we skip the first run.
  useEffect(() => {
    window.scrollTo(0, 0)

    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
        send_to: GA_MEASUREMENT_ID,
      })
    }
  }, [pathname, search])

  return (
    <div className="min-h-screen bg-cream-200">
      {/* Fixed Header/Navigation */}
      <Header />

      {/* Main Content - Rendered by child routes */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Layout
